import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  addToCart as apiAddToCart,
  removeFromCart as apiRemoveFromCart,
} from '@/lib/ecommerce/api'
import { resolveUnitPrice } from '@/lib/ecommerce/price'
import { useAuth } from '@/hooks/useAuth'
import type { Cart, CartItem } from '@/types/ecommerce'

/*
  Shared cart state hook.

  The addon drives the backing store server-side — every mutation here calls
  a same-origin /api endpoint and re-reads the cart from the response. A
  BroadcastChannel keeps multiple tabs in sync without polling. Components
  that only need the count (header bag badge) use `useCartCount()` to avoid
  re-rendering on every item update.

  Until the addon's API endpoints return the full cart shape we re-fetch via
  a lightweight GET to /cart (which mounts the same cart payload the cart
  page uses). That fallback pulls the server truth without needing a
  dedicated fetch-cart endpoint. The MIG cart wrapper page returns the
  cart as an embedded JSON payload.
*/

let sharedCart: Cart | null = null
let hydrationPromise: Promise<Cart | null> | null = null
const listeners = new Set<(cart: Cart | null) => void>()

/* MIG navigates via full page reloads (React inside Liquid, not a SPA),
   so `sharedCart` is null at every page boot. Without a synchronous
   way to know the cart count on first render, the header bag flickers
   on then the burger drops to make room. Mirror the count into
   sessionStorage so the first render of the next page already knows
   whether the bag should be visible — no flicker, no burger jump. */
const CART_COUNT_STORAGE_KEY = 'mig-cart-count'
function readCachedCount(): number {
  if (typeof window === 'undefined') return 0
  try {
    const raw = window.sessionStorage.getItem(CART_COUNT_STORAGE_KEY)
    const n = raw == null ? NaN : parseInt(raw, 10)
    return Number.isFinite(n) && n >= 0 ? n : 0
  } catch {
    return 0
  }
}
function writeCachedCount(count: number) {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(CART_COUNT_STORAGE_KEY, String(Math.max(0, count)))
  } catch {
    /* ignore (e.g. private-mode quota) */
  }
}
const channel: BroadcastChannel | null =
  typeof window !== 'undefined' && 'BroadcastChannel' in window
    ? new BroadcastChannel('mig-cart')
    : null

if (channel) {
  channel.onmessage = (e) => {
    const cart = (e.data as { cart: Cart | null } | null)?.cart ?? null
    sharedCart = cart
    const count = (cart?.cart_items ?? []).reduce(
      (n, it) => n + (it.quantity ?? 0),
      0,
    )
    writeCachedCount(count)
    listeners.forEach((fn) => fn(cart))
  }
}

function broadcast(cart: Cart | null) {
  sharedCart = cart
  const count = (cart?.cart_items ?? []).reduce(
    (n, it) => n + (it.quantity ?? 0),
    0,
  )
  writeCachedCount(count)
  listeners.forEach((fn) => fn(cart))
  channel?.postMessage({ cart })
}

async function fetchCartFromServer(): Promise<Cart | null> {
  // Temporary: re-use the cart page's JSON payload emitted in the
  // `page-data-json` script tag. When the addon ships a dedicated
  // GET /api/cart endpoint we swap this for a cleaner fetch.
  try {
    const res = await fetch('/cart', { credentials: 'same-origin' })
    if (!res.ok) return null
    const html = await res.text()
    const match = html.match(
      /<script[^>]*id="page-data-json"[^>]*>([\s\S]*?)<\/script>/,
    )
    if (!match) return null
    const payload = JSON.parse(match[1])
    return (payload?.cart as Cart) ?? null
  } catch {
    return null
  }
}

export interface UseCartResult {
  cart: Cart | null
  items: CartItem[]
  itemCount: number
  subtotal: number
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  addItem: (input: { product_uuid: string; quantity?: number; product_variant_uuid?: string | null }) => Promise<void>
  removeItem: (cart_item_uuid: string) => Promise<void>
}

export function useCart(initialCart?: Cart | null): UseCartResult {
  const { contactUuid } = useAuth()
  const [cart, setCart] = useState<Cart | null>(() => initialCart ?? sharedCart ?? null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const listener = (next: Cart | null) => setCart(next)
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  // Hydrate the cart once per full page load. Without this, after a hard
  // refresh sharedCart is null and components that read `items` (like
  // ArtworkDetail's "Already in Cart" check) think the cart is empty
  // until the user does something that triggers refresh(). The
  // hydrationPromise guard dedupes when several components mount at once.
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (initialCart || sharedCart) return
    if (!hydrationPromise) {
      hydrationPromise = fetchCartFromServer()
        .then((fresh) => {
          broadcast(fresh)
          return fresh
        })
        .catch(() => null)
        .finally(() => {
          hydrationPromise = null
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const fresh = await fetchCartFromServer()
      broadcast(fresh)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load cart.')
    } finally {
      setLoading(false)
    }
  }, [])

  const addItem: UseCartResult['addItem'] = useCallback(
    async (input) => {
      if (!contactUuid) {
        const msg = 'Cannot add to cart: visitor identifier is missing. Please reload the page and try again.'
        setError(msg)
        throw new Error(msg)
      }
      setLoading(true)
      setError(null)
      try {
        await apiAddToCart({
          product_uuid: input.product_uuid,
          product_variant_uuid: input.product_variant_uuid ?? null,
          quantity: input.quantity ?? 1,
          contact_uuid: contactUuid,
        })
        await refresh()
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not add item to cart.')
        throw e
      } finally {
        setLoading(false)
      }
    },
    [contactUuid, refresh],
  )

  const removeItem: UseCartResult['removeItem'] = useCallback(
    async (cart_item_uuid) => {
      if (!contactUuid) throw new Error('Visitor identifier is missing.')
      const target = (sharedCart ?? cart)?.cart_items.find((it) => it.uuid === cart_item_uuid)
      if (!target) throw new Error('Cart item not found.')
      setLoading(true)
      setError(null)
      try {
        await apiRemoveFromCart({
          id: target.id,
          uuid: target.uuid,
          cart_uuid: target.cart_uuid,
          contact_uuid: contactUuid,
        })
        await refresh()
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not remove item.')
        throw e
      } finally {
        setLoading(false)
      }
    },
    [cart, contactUuid, refresh],
  )

  const derived = useMemo(() => {
    const items = cart?.cart_items ?? []
    const itemCount = items.reduce((n, it) => n + (it.quantity ?? 0), 0)
    const subtotal = items.reduce((sum, it) => {
      const unit = resolveUnitPrice(it.product)
      return sum + unit * (it.quantity ?? 0)
    }, 0)
    return { items, itemCount, subtotal }
  }, [cart])

  return {
    cart,
    ...derived,
    loading,
    error,
    refresh,
    addItem,
    removeItem,
  }
}

/** Lightweight read-only hook for the header bag badge. */
export function useCartCount(initialCart?: Cart | null): number {
  const [count, setCount] = useState<number>(() => {
    const base = initialCart ?? sharedCart
    if (base) {
      return (base.cart_items ?? []).reduce((n, it) => n + (it.quantity ?? 0), 0)
    }
    /* No live cart yet on this fresh page load — fall back to the
       cached count from the previous page so the bag renders in the
       correct visible/hidden state on first paint. The real fetch
       (triggered by the cart page or any useCart() consumer) will
       reconcile this value within a few hundred ms. */
    return readCachedCount()
  })
  useEffect(() => {
    const listener = (next: Cart | null) => {
      setCount((next?.cart_items ?? []).reduce((n, it) => n + (it.quantity ?? 0), 0))
    }
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])
  return count
}
